import { useState } from 'react';
import styled from '@emotion/styled';
import LinkButton from 'storybookConfig/stories/molecules/Link/LinkButton';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'store/index';
import { closeCategories, closeMobileMenu } from 'store/slices/sidebar';
import { useMediaQuery } from 'usehooks-ts';
import { NAV_IS_MOBILE_MAX_WIDTH } from 'utils/constants';
import { Button } from 'react-bootstrap';
import Divider from 'storybookConfig/stories/molecules/Divider';
import Body from 'storybookConfig/stories/molecules/Body';

interface BodyWrapperProps {
  isMobile: boolean;
  children: React.ReactNode;
}

const BodyWrapper = ({ isMobile, children }: BodyWrapperProps) => {
  return <Body variant={isMobile ? 'Body/Regular' : 'Body/Body Small'}>{children}</Body>;
};

const CATEGORY_WIDTH = 220;

const calculateLeft = (level: number, isMobile: boolean) => {
  if (isMobile) return 0;
  if (level === 1) return 250;
  return 250 + (level - 1) * CATEGORY_WIDTH;
};

const CategoriesContainer = styled.div<{ level: number; isMobile: boolean }>`
  position: fixed;
  top: 0;
  left: ${({ level, isMobile }) => calculateLeft(level, isMobile)}px;
  width: ${({ isMobile }) => (isMobile ? '100%' : `${CATEGORY_WIDTH}px`)};
  height: 100vh;
  background-color: white;
  border-left: 1px solid #ddd;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: ${({ theme }) => theme.color.gray500};
  z-index: 1000;
  overflow-y: auto;
  @media (min-width: 768px) {
    gap: 8px;
  }
`;

interface StyledSpanProps {
  hovered: boolean;
}

const StyledSpan = styled.span<StyledSpanProps>`
  font-weight: ${({ hovered }) => (hovered ? 800 : 'inherit')};
  color: ${({ hovered, theme }) => (hovered ? theme.color.blue500 : 'inherit')};
`;

interface CategoryNode {
  code: string;
  children: Record<string, CategoryNode> | null;
  category: string;
}

interface CategoriesProps {
  level: number;
  data: CategoryNode;
  hoveredCategories: CategoryNode[];
  setHoveredCategories: React.Dispatch<React.SetStateAction<CategoryNode[]>>;
}

const Categories = ({
  level = 1,
  data,
  hoveredCategories,
  setHoveredCategories,
}: CategoriesProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery(`(max-width: ${NAV_IS_MOBILE_MAX_WIDTH}px)`);
  const handleRoute = (id: string) => {
    navigate(`/marketplace/category/${id}`);
    dispatch(closeCategories());
    if (isMobile) {
      dispatch(closeMobileMenu());
    }
  };

  const handleHover = (child: CategoryNode) => {
    setHoveredCategories((prev) => {
      const newHovered = [...prev];
      newHovered[level] = child;
      return newHovered.slice(0, level + 1);
    });
  };

  const handleMobileBack = (currentLevel: number) => {
    if (currentLevel === 1) {
      dispatch(closeCategories());
    }
    const newHovered = [...hoveredCategories];
    newHovered.pop();
    setHoveredCategories(newHovered);
  };

  return (
    <>
      <CategoriesContainer level={level} isMobile={isMobile}>
        {isMobile && (
          <>
            <Button onClick={() => handleMobileBack(level)}>Back</Button>
            <Divider margin="0 16px" />
          </>
        )}

        {level > 1 && (
          <LinkButton onClick={() => handleRoute(data.code)}>
            <BodyWrapper isMobile={isMobile}>All {data.category}</BodyWrapper>
          </LinkButton>
        )}
        {data?.children != null &&
          Object.values(data.children)?.map((child) => {
            return (
              <div key={child.code}>
                {Object.keys(child.children || {})?.length > 0 && level < 3 ? (
                  <BodyWrapper isMobile={isMobile}>
                    <LinkButton onClick={() => handleRoute(child.code)}>
                      <StyledSpan
                        onMouseEnter={() => handleHover(child)}
                        hovered={hoveredCategories[level]?.code === child.code}
                      >
                        {child.category}
                      </StyledSpan>
                    </LinkButton>
                  </BodyWrapper>
                ) : (
                  <LinkButton onClick={() => handleRoute(child.code)}>
                    <BodyWrapper isMobile={isMobile}>{child.category}</BodyWrapper>
                  </LinkButton>
                )}
              </div>
            );
          })}
      </CategoriesContainer>

      {hoveredCategories[level] && hoveredCategories[level].children && (
        <Categories
          level={level + 1}
          data={hoveredCategories[level]}
          hoveredCategories={hoveredCategories}
          setHoveredCategories={setHoveredCategories}
        />
      )}
    </>
  );
};

interface CategoryWrapperProps {
  data: CategoryNode;
}

const CategoryWrapper = ({ data }: CategoryWrapperProps) => {
  const [hoveredCategories, setHoveredCategories] = useState<CategoryNode[]>([]);

  return (
    <Categories
      level={1}
      data={data}
      hoveredCategories={hoveredCategories}
      setHoveredCategories={setHoveredCategories}
    />
  );
};

export default CategoryWrapper;
